import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import App from './App';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './hooks/useConfirm';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/theme.css';
import './styles/app.css';

// The saved table-density preference must apply before the first paint, or the
// rows would visibly resize a moment after the page appears.
try {
  const density = localStorage.getItem('mdg.density');
  if (density) document.documentElement.setAttribute('data-density', density);
} catch {
  // Storage can be disabled; the default density is fine.
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin data changes through this console, and mutations invalidate what
      // they touch, so background refetching on every window focus is noise.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying an auth or validation failure just delays the real message.
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <App />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#344767',
                  borderRadius: '10px',
                  boxShadow: '0 12px 28px rgba(20,20,43,.18)',
                  fontSize: '13px',
                  fontWeight: 600,
                  maxWidth: '420px',
                },
                success: { iconTheme: { primary: '#2e7d4f', secondary: '#fff' } },
                error: { duration: 6000, iconTheme: { primary: '#c62828', secondary: '#fff' } },
              }}
            />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
